import type { UiReviewQueueItem } from '@nightshift/ui-types';

const SENSITIVE_LABEL: Record<NonNullable<UiReviewQueueItem['sensitivePaths']>[number]['kind'], string> = {
  pipeline: 'pipeline config',
  ci: 'CI',
  'runner-script': 'script',
  lockfile: 'lockfile',
  migration: 'migration',
  credentials: 'credentials',
};

function count(value: number, one: string, many = `${one}s`): string {
  return `${value} ${value === 1 ? one : many}`;
}

export function sizeLabel(item: UiReviewQueueItem): string {
  if (item.changedLines === null) return 'size not recorded';
  const files = item.filesChanged === null ? '' : ` in ${count(item.filesChanged, 'file')}`;
  return `${count(item.changedLines, 'line')}${files}${item.binary ? ' + binary' : ''}`;
}

export function sensitiveLabel(item: UiReviewQueueItem): string {
  if (item.sensitivePaths === null) return 'paths not recorded';
  if (item.sensitivePaths.length === 0) return 'no sensitive paths';
  const kinds = [...new Set(item.sensitivePaths.map((row) => SENSITIVE_LABEL[row.kind]))];
  return `${count(item.sensitivePaths.length, 'sensitive path')} (${kinds.join(', ')})`;
}

export function bounceLabel(item: UiReviewQueueItem): string {
  return item.bounces === 0 ? 'no bounces' : count(item.bounces, 'bounce');
}

export function reviewerLabel(item: UiReviewQueueItem): string {
  if (item.reviewers.length === 0) return 'no verdicts';
  return item.reviewers
    .map((row) => {
      const notes = [row.errors > 0 ? count(row.errors, 'error') : null, row.warnings > 0 ? count(row.warnings, 'warning') : null]
        .filter(Boolean)
        .join(', ');
      return `${row.name} ${row.verdict}${notes ? ` (${notes})` : ''}`;
    })
    .join('; ');
}

/** The one line a person triages on: size, sensitive paths, bounces, verdicts. */
export function reviewRiskSummary(item: UiReviewQueueItem): string {
  return [sizeLabel(item), sensitiveLabel(item), bounceLabel(item), reviewerLabel(item)].join(' · ');
}

/**
 * Whether this ticket deserves more than a glance. Not a verdict and not a gate: a highlight for
 * the rows where something is sensitive, unknown, or the change has already needed repair.
 */
export function needsCloseLook(item: UiReviewQueueItem): boolean {
  return (
    item.sensitivePaths === null ||
    item.sensitivePaths.length > 0 ||
    item.changedLines === null ||
    item.binary ||
    item.bounces > 0 ||
    item.reviewers.some((row) => row.verdict !== 'pass' || row.errors > 0)
  );
}

export interface ApprovalRequest {
  key: string;
  project: string;
  version: number;
}

/**
 * What a batch approval sends: every selected ticket still in the list, each with the version
 * the person saw. A ticket that moved since the list loaded is refused by the server on that
 * version rather than approved in a state nobody looked at.
 */
export function batchApprovals(items: readonly UiReviewQueueItem[], selected: ReadonlySet<number>): ApprovalRequest[] {
  return items
    .filter((item) => selected.has(item.ticketId))
    .map((item) => ({ key: item.key, project: item.projectSlug, version: item.version }));
}

export interface ApprovalOutcome {
  key: string;
  ok: boolean;
  message: string;
}

export function batchOutcomeMessage(outcomes: readonly ApprovalOutcome[]): { tone: 'good' | 'bad'; message: string } {
  const approved = outcomes.filter((row) => row.ok);
  const refused = outcomes.filter((row) => !row.ok);
  if (refused.length === 0) return { tone: 'good', message: `Approved ${count(approved.length, 'ticket')} — landing now` };
  const detail = refused.map((row) => `${row.key}: ${row.message}`).join('; ');
  return {
    tone: 'bad',
    message: `Approved ${approved.length} of ${outcomes.length}. Not approved — ${detail}`,
  };
}
